import { Field, Form, Formik, FormikProps } from 'formik';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import { usePhoneOptionsContext } from '../../../../context/PhoneOptionsContext';
import ToastContext from '../../../../context/ToastContext';
import useAxios from '../../../../hooks/useAxios';
import usePrevious from '../../../../hooks/usePrevious';
import { ErrorResponse, WithData } from '../../../../types/api';
import {
  MunicipalityCollection,
  MunicipalityPlaceCollection,
} from '../../../../types/api/municipalities';
import { PlaceStreetCollection } from '../../../../types/api/places';
import { LabelValue } from '../../../../types/options';
import { isStreetFreeInputAllowed } from '../../../../utils/constants/misc';
import { invalidPhoneNumberCharactersRegex } from '../../../../utils/constants/phoneNumbers';
import {
  AllowedErrors,
  tryApiContextValidation,
} from '../../../../utils/helpers/formik';
import {
  placeItemTemplate,
  placeValueTemplate,
} from '../../../../utils/helpers/misc';
import {
  formatMobileNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../utils/helpers/phoneNumbers';
import { errorToast, successToast } from '../../../../utils/helpers/primereact';
import FieldWithErrors from '../../../Forms/FieldWithErrors/FieldWithErrors';
import UnauthPage from '../../../page/UnauthPage';
import {
  FormFields,
  getInitialValues,
  getValidationSchema,
} from './Register.functions';

function Register(): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();

  const formRef = useRef<FormikProps<FormFields>>(null);
  const { toastRef } = useContext(ToastContext);

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  const { data: municipalitiesData, isLoading: isMunicipalitiesDataLoading } =
    useAxios<WithData<MunicipalityCollection>>({
      url: '/municipalities?limit=0',
      headers: {
        'X-Client-Id': process.env.REACT_APP_CLIENT_ID,
      },
    });

  const {
    data: placesInMunicipalityData,
    reload: reloadPlacesInMunicipalityData,
    isLoading: isPlacesInMunicipalityDataLoading,
  } = useAxios<MunicipalityPlaceCollection>(
    {
      url: `/municipalities/${formRef?.current?.values.address.municipalityId}/places`,
      headers: {
        'X-Client-Id': process.env.REACT_APP_CLIENT_ID,
      },
    },
    {
      skipWhen: !formRef?.current?.values.address.municipalityId,
    }
  );

  const {
    data: streetsData,
    reload: reloadStreetsData,
    isLoading: isStreetsDataLoading,
  } = useAxios<WithData<PlaceStreetCollection>>(
    {
      url: `/places/${formRef?.current?.values.address.placeId}/streets?limit=0`,
      headers: {
        'X-Client-Id': process.env.REACT_APP_CLIENT_ID,
      },
    },
    {
      skipWhen: !formRef?.current?.values.address.placeId,
    }
  );

  const municipalityOptions = useMemo(
    () =>
      municipalitiesData?.data?.map((municipality) => ({
        value: municipality.id,
        label: municipality.name,
        city_id: municipality.city_id,
      })) ?? [],
    [municipalitiesData]
  );

  const placeOptions = useMemo(
    () =>
      placesInMunicipalityData?.map((place) => ({
        value: String(place.place_id),
        label: place.place_name,
        postal_code: place.postenski_broj,
      })) ?? [],
    [placesInMunicipalityData]
  );

  const streetOptions = useMemo<LabelValue[]>(
    () =>
      streetsData?.data?.map((street) => ({
        value: String(street.id),
        label: street.name,
      })) ?? [],
    [streetsData]
  );

  // Dialog state
  const [dialogVisibility, setDialogVisibility] = useState<boolean>(false);

  // User registration
  const {
    data: registerClientData,
    error: registerClientError,
    reload: registerClient,
  } = useAxios<any, ErrorResponse>();

  const registerClientDataPrevious = usePrevious(registerClientData);
  const registerClientErrorPrevious = usePrevious(registerClientError);

  useEffect(() => {
    if (
      !registerClientData ||
      registerClientData === registerClientDataPrevious ||
      !toastRef?.current
    ) {
      return;
    }

    successToast(
      toastRef,
      t('Successful User Registration'),
      t('Your account has been registered. Follow the email instructions.')
    );

    setDialogVisibility(true);
  }, [registerClientData, registerClientDataPrevious, t, toastRef]);

  const allowedErrors: AllowedErrors = useMemo(
    () => ({
      ime: {
        already_in_use: t('A client with this name already exists'),
        customName: 'name',
      },
      email: {
        duplicate: t('A client with this email already exists'),
        already_in_use: t('A client with this email already exists'),
      },
      mobilen: {
        already_in_use: t('A client with this mobile number already exists'),
        customName: 'phone',
      },
      danocen_broj: {
        combination_ssn_tax_already_in_use: t(
          'A client with this SSN - Tax No. combination already exists'
        ),
        customName: 'tax_number',
      },
    }),
    [t]
  );

  useEffect(() => {
    if (
      !registerClientError ||
      registerClientError === registerClientErrorPrevious ||
      !toastRef?.current
    ) {
      return;
    }

    const errorDescription =
      registerClientError.response?.data.error_description;

    if (errorDescription) {
      const validated = tryApiContextValidation(
        errorDescription,
        formRef as any,
        allowedErrors
      );

      if (!validated) {
        errorToast(
          toastRef,
          t('User Registration Failed'),
          t('An error occurred while trying to register user.')
        );
      }
    }
  }, [
    allowedErrors,
    registerClientError,
    registerClientErrorPrevious,
    t,
    toastRef,
  ]);

  function handleUserRegisration(values: FormFields) {
    let submitData: Record<string, any> = {
      ime: values.name,
      email: values.email,
      mobilen: formatMobileNumber(values.phone, values.phone_region),
      address: values.address,
    };

    if (values.legal_entity) {
      submitData['pravno_lice'] = Number(values.legal_entity);
      submitData['danocen_broj'] = values.tax_number;
    }

    registerClient({
      url: '/clients/register',
      method: 'POST',
      headers: {
        'X-Client-Id': process.env.REACT_APP_CLIENT_ID,
      },
      data: submitData,
    });
  }

  const initialValues = useMemo(() => getInitialValues(), []);

  function goToLogin() {
    history.push('/login');
  }

  return (
    <>
      <Dialog
        header={t('Successful User Registration')}
        visible={dialogVisibility}
        onHide={() => {
          setDialogVisibility(false);
        }}
        footer={<Button type="button" label={t('OK')} onClick={goToLogin} />}
      >
        <p>{t('Your account has been sucessfully registered.')}</p>
        <p>
          {t(
            'Your request for registration will be processed soon. For the status of your activation you will be notified by email.'
          )}
        </p>
      </Dialog>

      <UnauthPage
        title={t('Create an Account')}
        description={t('Enter your information to complete the registration.')}
      >
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          validateOnChange
          validationSchema={getValidationSchema(t)}
          onSubmit={handleUserRegisration}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="p-fluid">
                <div className="p-grid">
                  <FieldWithErrors
                    name="name"
                    label={t('Name')}
                    className="p-col"
                  >
                    <Field type="input" name="name" as={InputText} />
                  </FieldWithErrors>
                  <div className="p-d-flex p-ai-center p-mt-3">
                    <Field
                      type="checkbox"
                      name="legal_entity"
                      inputId="legal_entity"
                      as={Checkbox}
                    />
                    <label htmlFor="legal_entity">{t('Legal Entity')}</label>
                  </div>
                </div>

                <FieldWithErrors name="email" label={t('Email')}>
                  <Field type="email" name="email" as={InputText} />
                </FieldWithErrors>

                <FieldWithErrors name="phone" label={t('Mobile Number')}>
                  <div className="p-inputgroup">
                    <Field
                      as={Dropdown}
                      className="phone_region"
                      style={{ padding: 0, width: '5rem' }}
                      id="phone_region"
                      name="phone_region"
                      options={languages}
                      itemTemplate={countryOptionTemplate}
                      valueTemplate={selectedCountryTemplate}
                      data-cy="phone_region"
                    />
                    <Field
                      type="input"
                      style={{ width: '100%' }}
                      name="phone"
                      as={InputText}
                      value={formatMobileNumber(
                        values.phone,
                        values.phone_region ||
                          getPhoneorMobileNumberRegion(values.phone)
                      )}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setFieldValue(
                          'phone',
                          e.target.value.replace(
                            invalidPhoneNumberCharactersRegex,
                            ''
                          )
                        )
                      }
                    />
                  </div>
                </FieldWithErrors>

                {values.legal_entity && (
                  <FieldWithErrors name="tax_number" label={t('Tax Number')}>
                    <Field type="input" name="tax_number" as={InputText} />
                  </FieldWithErrors>
                )}

                <FieldWithErrors
                  name="address.municipalityId"
                  includeErrorsFor={['address.municipalityName']}
                  label={t('Municipality')}
                >
                  <Field
                    name="address.municipalityId"
                    inputId="address.municipalityId"
                    as={Dropdown}
                    filter
                    options={municipalityOptions}
                    placeholder={
                      isMunicipalitiesDataLoading
                        ? t('Loading...')
                        : t('Select Municipality')
                    }
                    disabled={isMunicipalitiesDataLoading}
                    onChange={(e: DropdownChangeParams) => {
                      setFieldValue('address.municipalityId', e.value);
                      const item = municipalityOptions?.find(
                        (s) => s.value === e.value
                      );
                      setFieldValue('pickup_address.placeName', item?.label);
                      setFieldValue('pickup_address.cityId', item?.city_id);

                      setFieldValue('address.placeId', null);
                      setFieldValue('address.placeName', '');
                      setFieldValue('address.streetId', null);
                      setFieldValue('address.streetName', '');
                      setFieldValue('address.housenumber', '');
                      setFieldValue('address.entrance', '');
                      setFieldValue('address.apartment', '');

                      reloadPlacesInMunicipalityData({
                        url: `municipalities/${e.value}/places`,
                      });
                    }}
                    className="data-cy-municipality"
                  />
                </FieldWithErrors>

                <FieldWithErrors
                  name="address.placeId"
                  includeErrorsFor={['address.placeName']}
                  label={t('Place')}
                >
                  <Field
                    type="input"
                    name="address.placeId"
                    inputId="address.placeId"
                    as={Dropdown}
                    filter
                    options={placeOptions}
                    itemTemplate={placeItemTemplate}
                    valueTemplate={placeValueTemplate}
                    placeholder={
                      isPlacesInMunicipalityDataLoading ? t('Loading...') : ''
                    }
                    onChange={(e: DropdownChangeParams) => {
                      setFieldValue('address.placeId', e.value);
                      const item = placeOptions?.find(
                        (s) => s.value === e.value
                      );
                      setFieldValue('address.placeName', item?.label);
                      setFieldValue('address.postcode', item?.postal_code);

                      setFieldValue('address.streetId', null);
                      setFieldValue('address.streetName', '');
                      setFieldValue('address.housenumber', '');
                      setFieldValue('address.entrance', '');
                      setFieldValue('address.apartment', '');

                      reloadStreetsData({
                        url: `places/${e.value}/streets?limit=0`,
                      });
                    }}
                    disabled={
                      !values.address.municipalityId ||
                      isPlacesInMunicipalityDataLoading
                    }
                    className="data-cy-place"
                  />
                </FieldWithErrors>

                {isStreetFreeInputAllowed ? (
                  <FieldWithErrors
                    name="address.rawName"
                    label={
                      isStreetFreeInputAllowed ? t('Address') : t('Street')
                    }
                  >
                    <Field
                      as={InputText}
                      name="address.rawName"
                      id="address.rawName"
                      disabled={
                        !values.address.municipalityId ||
                        !values.address.placeId
                      }
                      maxLength="256"
                    />
                  </FieldWithErrors>
                ) : (
                  <>
                    <FieldWithErrors
                      name="address.streetId"
                      includeErrorsFor={['address.streetName']}
                      label={t('Street')}
                    >
                      <Field
                        type="input"
                        name="address.streetId"
                        inputId="address.streetId"
                        as={Dropdown}
                        filter
                        options={streetOptions}
                        disabled={
                          !values.address.placeId || isStreetsDataLoading
                        }
                        placeholder={
                          isStreetsDataLoading ? t('Loading...') : ''
                        }
                        className="data-cy-street"
                        onChange={(e: DropdownChangeParams) => {
                          setFieldValue('address.streetId', e.value);
                          const item = streetOptions?.find(
                            (s) => s.value === e.value
                          );
                          setFieldValue('address.streetName', item?.label);

                          setFieldValue('address.housenumber', '');
                          setFieldValue('address.entrance', '');
                          setFieldValue('address.apartment', '');
                        }}
                      />
                    </FieldWithErrors>
                    <div className="p-grid">
                      <div className="p-col">
                        <FieldWithErrors
                          name="address.housenumber"
                          label={t('Number')}
                        >
                          <Field
                            type="input"
                            name="address.housenumber"
                            as={InputText}
                            disabled={!values.address.streetId}
                          />
                        </FieldWithErrors>
                      </div>

                      <div className="p-col">
                        <FieldWithErrors
                          name="entrance_number"
                          label={t('Entrance No.')}
                        >
                          <Field
                            type="input"
                            name="address.entrance"
                            as={InputText}
                            disabled={!values.address.streetId}
                          />
                        </FieldWithErrors>
                      </div>

                      <div className="p-col">
                        <FieldWithErrors
                          name="address.apartment"
                          label={t('Flat No.')}
                        >
                          <Field
                            type="input"
                            name="address.apartment"
                            as={InputText}
                            disabled={!values.address.streetId}
                          />
                        </FieldWithErrors>
                      </div>
                    </div>
                  </>
                )}

                <p className="p-mt-0 p-mb-0 p-text-secondary p-text-light">
                  {t('Already have an account?')}{' '}
                  <Link to="/login">{t('Login')}</Link>
                </p>

                <Button
                  type="submit"
                  label={t('Register')}
                  className="p-mt-2"
                />
              </div>
            </Form>
          )}
        </Formik>
      </UnauthPage>
    </>
  );
}

export default Register;
